/**
 * 排课工作台（class-scheduling change 5-4，ADM-19/20/21；单课次调整/取消 2026-09-18 扩展）。
 *
 * 严格对齐原型 prototype/admin/src/pages/classes/ScheduleWorkbenchPage.tsx：
 * - 周历 7 天列（校区筛选 + 前后周切换 + 空态「无课次」），课次卡片字段与
 *   「教室已调整」角标、发布预览文案（含通知对象）、时长默认 45/步进 5/minuteStep 15、
 *   新教室选项「｛名｝（容量｛N｝）」格式均以原型为准。
 * - 周历经 excludeCancelled 隐藏已取消课次（员工/家长课表仍保留展示取消标识）；
 *   SCHEDULED 卡片提供「取消」danger 操作（Modal.confirm 二次确认，scheduling-workbench-ui:SC-24）。
 * - 单课次调整弹窗三字段（上课日期/开始时间/教室，预填当前值，全量提交；
 *   非「待上课」课次表单禁用，scheduling-workbench-ui:SC-09/SC-25/SC-26）。
 * - 硬冲突 409 → error.details.conflicts 逐条渲染红色 Alert（表单保留）；软提醒随成功
 *   warning 提示；成功后周历刷新。
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, DatePicker, Form, InputNumber, Popconfirm, Row, Select, Space, TimePicker, Typography } from 'antd';
import { message } from '../../utils/antd-message';
import { DraggableModal } from "../../components/DraggableModal";
import { useDraggableModal } from "../../components/useDraggableModal";
import { LeftOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";
import { ApiError } from "../../api/transport";
import { SoftWarningsAlert, type SoftWarningItem } from "./SoftWarningsAlert";
import { classesApi } from "../../api/classes.api";
import { classroomsApi } from "../../api/classrooms.api";
import { staffApi, weekdayLabel } from "../../api/staff.api";
import {
  adjustLessonSession,
  cancelLessonSession,
  endSchedule,
  getNotificationPreview,
  listLessonSessions,
  listSchedules,
  publishSchedules,
  type LessonSessionListItem,
  type NotificationPreview,
  type ScheduleConflictType,
} from "../../api/scheduling.api";
import { Tag } from "antd";
import { HasPermission, useHasPermission } from "../../auth/HasPermission";
import { useGlobalCampusScope } from "../../auth/GlobalCampusContext";

const { Title, Text } = Typography;

/** 冲突类型中文（ADM-20 明细）。 */
const conflictTypeLabel: Record<ScheduleConflictType, string> = {
  CLASS: "班级时间冲突",
  CLASSROOM: "教室占用冲突",
  TEACHER: "教师占用冲突",
  CAPACITY: "教室容量不足",
};

/** 课次状态标签（对齐原型 LessonSessionStatusTag；学生详情浮层课表 Tab 复用）。 */
export function SessionStatusTag({ status }: { status: string }) {
  if (status === "SCHEDULED") return <Tag color="processing">待上课</Tag>;
  if (status === "COMPLETED") return <Tag color="success">已完成</Tag>;
  return <Tag>已取消</Tag>;
}

interface ClassOption {
  id: string;
  name: string;
  campusId: string;
  teacherId: string;
  defaultClassroomId: string | null;
  /** 班级有效期（课次生成窗口 = 排课有效期 ∩ 班级有效期，对齐后端 expandSchedule） */
  startDate: string;
  endDate: string;
}

interface ClassroomOption {
  id: string;
  name: string;
  capacity: number;
  campusId: string;
}

export function ScheduleWorkbenchPage() {
  const draggableModal = useDraggableModal();
  // 周历锚点：ISO 周一（dayjs 默认周日为周首，startOf("week") 在周日会算出
  // 下周一致初始视图跳到下周、当天课次不可见——2026-09-20 周日实测 4 用例红）
  const [weekStart, setWeekStart] = useState<Dayjs>(() =>
    dayjs().subtract((dayjs().day() + 6) % 7, "day").startOf("day"),
  );
  const [sessions, setSessions] = useState<LessonSessionListItem[]>([]);
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [classrooms, setClassrooms] = useState<ClassroomOption[]>([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);

  // 单课次调整（ADM-21，三字段 Form：上课日期/开始时间/教室，预填当前值）
  const [adjustTarget, setAdjustTarget] =
    useState<LessonSessionListItem | null>(null);
  const [adjustForm] = Form.useForm();
  // 单课次调整/取消=ADM-20 EDIT（教师矩阵含此组合）、结束排课=ADM-19 EDIT，均对齐后端 @Roles
  const canAdjustSession = useHasPermission("ADM-20", "EDIT");
  const canEndSchedule = useHasPermission("ADM-19", "EDIT");
  const [adjusting, setAdjusting] = useState(false);
  /** 调整撞硬冲突明细（弹层保留已录入选择，逐条展示跟谁冲突，SC-18 同口径）。 */
  const [adjustConflicts, setAdjustConflicts] = useState<
    Array<{
      type: ScheduleConflictType;
      dayOfWeek: number;
      description: string;
    }>
  >([]);

  /** 发布软提醒（常驻页面，点「知道了」关闭；每次发布覆盖为最新，T03 规则）。 */
  const [softWarnings, setSoftWarnings] = useState<SoftWarningItem[]>([]);

  // 结束排课（班级详情抽屉入口之外的工作台快捷口径：从周历下钻当前班级规则）
  const [rulesOfClass, setRulesOfClass] = useState<
    Array<{
      id: string;
      dayOfWeek: number;
      startTime: string;
      classroomName: string;
      status: string;
      futureScheduledSessionCount: number;
    }>
  >([]);
  const [rulesClassId, setRulesClassId] = useState<string | null>(null);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day")),
    [weekStart],
  );
  // 全局校区范围：'ALL' 不携带 campusId（锁定模式，排课工作台不设页内校区控件）
  const { scope } = useGlobalCampusScope();

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      // 排课工作台周历隐藏已取消课次（scheduling-workbench-ui:SC-23；
      // 员工/家长课表与学生详情课表不受影响，仍保留展示取消标识）
      const items = await listLessonSessions(
        days[0]!.format("YYYY-MM-DD"),
        days[6]!.format("YYYY-MM-DD"),
        undefined,
        { excludeCancelled: true, campusId: scope === "ALL" ? undefined : scope },
      );
      setSessions(items);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "周历加载失败");
    } finally {
      setLoading(false);
    }
  }, [days, scope]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    // 班级/教室选项（一次拉取，均随全局校区口径）；教师与新建排课弹窗教室选项移入
    // 弹窗按所选班级校区加载（O-28 校区隔离：选项随全局校区，锁定页数据口径 rule ②
    // 同源；O-4 补录：页面级教室预载纳入锁定口径，非「全部校区」携带 campusId）
    void (async () => {
      try {
        const [classPage, roomPage] = await Promise.all([
          // 排课仅面向未结课班级（服务端按终态集合过滤，与发布校验 SC-05 同源）
          classesApi.listClasses({
            page: 1,
            pageSize: 100,
            excludeEnded: true,
            campusId: scope === "ALL" ? undefined : scope,
          }), // 分页上限 100（后端 MAX_PAGE_SIZE）
          classroomsApi.listClassrooms({
            page: 1,
            pageSize: 100,
            campusId: scope === "ALL" ? undefined : scope,
          }),
        ]);
        setClasses(
          classPage.items.map((c) => ({
            id: c.id,
            name: c.name,
            campusId: c.campusId,
            teacherId: c.teacherId,
            defaultClassroomId: c.defaultClassroomId,
            startDate: c.startDate,
            endDate: c.endDate,
          })),
        );
        setClassrooms(
          roomPage.items.map((r) => ({
            id: r.id,
            name: r.name,
            capacity: r.capacity,
            campusId: r.campusId,
          })),
        );
      } catch {
        message.error("基础数据加载失败，请刷新重试");
      }
    })();
  }, [scope]);

  const sessionsByDay = useMemo(() => {
    const map = new Map<string, LessonSessionListItem[]>();
    for (const day of days) {
      const dateStr = day.format("YYYY-MM-DD");
      map.set(
        dateStr,
        sessions.filter((s) => s.sessionDate === dateStr),
      );
    }
    return map;
  }, [sessions, days]);


  /** 单课次调整（ADM-21：三字段全量提交；冲突提示不关闭弹层，对齐原型 master 版）。 */
  async function handleAdjust() {
    if (!adjustTarget) return;
    const values = await adjustForm.validateFields();
    setAdjusting(true);
    setAdjustConflicts([]);
    try {
      await adjustLessonSession(adjustTarget.id, {
        sessionDate: (values.sessionDate as Dayjs).format("YYYY-MM-DD"),
        startTime: (values.startTime as Dayjs).format("HH:mm"),
        classroomId: values.classroomId as string,
      });
      message.success("已保存调整");
      setAdjustTarget(null);
      await loadSessions();
    } catch (err) {
      if (err instanceof ApiError && err.code === "scheduling.hard_conflict") {
        const detail = err.details as
          | {
              conflicts?: Array<{
                type: ScheduleConflictType;
                dayOfWeek: number;
                description: string;
              }>;
            }
          | undefined;
        // 弹层保留已录入选择，Modal 内逐条展示冲突明细（不弹 toast）
        setAdjustConflicts(detail?.conflicts ?? []);
      } else if (!(err instanceof ApiError)) {
        message.error("调整失败");
      } else {
        message.error(err.message);
      }
    } finally {
      setAdjusting(false);
    }
  }

  /** 取消单课次（卡片 danger 操作，命令式确认弹窗二次确认，scheduling-workbench-ui:SC-24）。 */
  function confirmCancelSession(session: LessonSessionListItem) {
    draggableModal.confirm({
      title: "取消课次",
      content: `确定取消 ${session.className} ${session.sessionDate} ${dayjs(session.startAt).format("HH:mm")} 的课次？取消后不可恢复，员工与家长课表将展示取消标识。`,
      okText: "取消课次",
      okButtonProps: { danger: true },
      cancelText: "返回",
      onOk: async () => {
        try {
          await cancelLessonSession(session.id, true);
          message.success("已取消课次");
          await loadSessions();
        } catch (err) {
          message.error(err instanceof ApiError ? err.message : "取消失败");
        }
      },
    });
  }

  /** 结束排课（Popconfirm 展示影响数，design D8/SC-35）。 */
  async function handleEndRule(ruleId: string, futureCount: number) {
    try {
      const result = await endSchedule(ruleId, true);
      message.success(
        `已结束排课，取消未来 ${result.cancelledSessionCount} 个课次`,
      );
      void futureCount;
      if (rulesClassId) {
        setRulesOfClass(await listSchedules(rulesClassId));
      }
      await loadSessions();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "结束排课失败");
    }
  }

  /** 周历卡片点击 → 单课次调整（三字段预填当前值）；无调整权限时点击不打开（后端 403 兜底）。 */
  async function openSessionAction(session: LessonSessionListItem) {
    if (!canAdjustSession) return;
    setAdjustTarget(session);
    setAdjustConflicts([]);
    // 同步加载该班级规则（供结束排课快捷操作，展示与班级详情一致的派生计数）
    try {
      const rules = await listSchedules(session.classId);
      setRulesOfClass(rules);
      setRulesClassId(session.classId);
    } catch {
      setRulesOfClass([]);
    }
  }

  // 调整弹窗预填：Form 挂载后（内容随 {adjustTarget && ...} 门控）再写值——
  // 关窗态调 setFieldsValue 会触发「useForm not connected」开发告警
  useEffect(() => {
    if (!adjustTarget) return;
    adjustForm.setFieldsValue({
      sessionDate: dayjs(adjustTarget.sessionDate),
      startTime: dayjs(adjustTarget.startAt),
      classroomId: adjustTarget.classroomId,
    });
  }, [adjustTarget, adjustForm]);

  const adjustClass = classes.find((c) => c.id === adjustTarget?.classId);
  const campusRooms = classrooms.filter(
    (r) => !adjustClass || r.campusId === adjustClass.campusId,
  );

  return (
    <div>
      <div className="page-header-row">
        <div>
          <Title level={3}>排课工作台</Title>
          <p className="page-header-desc">
            以周历查看课次分布，新建周期排课支持一次选择多天、每天单独设置时间；预检班级/教师/教室三类硬冲突，超出教师可排课时段仅作软提醒。
          </p>
        </div>
        {/* 发布排课需 ADM-19 CREATE（教师仅课次调整 ADM-20 EDIT，无发布入口）；越权由后端 403 兜底 */}
        <HasPermission menuKey="ADM-19" action="CREATE">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
          >
            排课
          </Button>
        </HasPermission>
      </div>
      <SoftWarningsAlert
        warnings={softWarnings}
        onAcknowledge={() => setSoftWarnings([])}
      />

      <div className="page-filter-card">
        <Space wrap size={12}>
          <Button
            icon={<LeftOutlined />}
            onClick={() => setWeekStart(weekStart.subtract(7, "day"))}
          />
          <Text strong>
            {days[0]!.format("YYYY-MM-DD")} 至 {days[6]!.format("YYYY-MM-DD")}
          </Text>
          <Button
            icon={<RightOutlined />}
            onClick={() => setWeekStart(weekStart.add(7, "day"))}
          />
        </Space>
      </div>

      <Row gutter={8}>
        {days.map((day) => {
          const dateStr = day.format("YYYY-MM-DD");
          const daySessions = sessionsByDay.get(dateStr) ?? [];
          return (
            <Col span={24 / 7} key={dateStr}>
              <Card
                size="small"
                title={`${weekdayLabel[day.day() === 0 ? 7 : day.day()]} ${day.format("MM-DD")}`}
                loading={loading}
                style={{ minHeight: 260, marginBottom: 12 }}
              >
                {daySessions.length === 0 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    无课次
                  </Text>
                )}
                {daySessions.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => void openSessionAction(s)}
                    style={{
                      background:
                        "var(--proto-color-primary-light, rgba(22,119,255,0.06))",
                      borderRadius: 6,
                      padding: 6,
                      marginBottom: 6,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600 }}>
                      {dayjs(s.startAt).format("HH:mm")}
                    </div>
                    <div style={{ fontSize: 12 }}>{s.className}</div>
                    <div style={{ fontSize: 11, color: "#666" }}>
                      {s.classroomName}
                    </div>
                    <Space size={4} style={{ marginTop: 2 }} wrap>
                      <SessionStatusTag status={s.status} />
                      {s.classroomChanged && (
                        <span style={{ fontSize: 11, color: "#e8791a" }}>
                          教室已调整
                        </span>
                      )}
                      {s.status === "SCHEDULED" && canAdjustSession && (
                        <Button
                          type="text"
                          danger
                          size="small"
                          style={{ fontSize: 11, padding: "0 4px", height: 20 }}
                          onClick={(e) => {
                            // 阻止冒泡，避免触发卡片点击打开调整弹窗
                            e.stopPropagation();
                            confirmCancelSession(s);
                          }}
                        >
                          取消
                        </Button>
                      )}
                    </Space>
                  </div>
                ))}
              </Card>
            </Col>
          );
        })}
      </Row>

      <PublishScheduleModal
        open={modalOpen}
        classes={classes}
        onClose={() => setModalOpen(false)}
        onPublished={setSoftWarnings}
        onRefresh={loadSessions}
      />

      <DraggableModal
        open={!!adjustTarget}
        title="单课次调整"
        onCancel={() => setAdjustTarget(null)}
        onOk={() => void handleAdjust()}
        okText="保存调整"
        cancelText="取消"
        confirmLoading={adjusting}
        okButtonProps={{ disabled: adjustTarget?.status !== "SCHEDULED" }}
      >
        {adjustTarget && (
          <>
            {adjustConflicts.length > 0 && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 12 }}
                message="存在硬冲突，无法调整"
                description={
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {adjustConflicts.map((c, i) => (
                      <li key={i}>
                        【{conflictTypeLabel[c.type]}·
                        {weekdayLabel[c.dayOfWeek]}】{c.description}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
            <p>
              课次：{adjustTarget.className} ·{" "}
              {dayjs(adjustTarget.startAt).format("YYYY-MM-DD HH:mm")} ~{" "}
              {dayjs(adjustTarget.endAt).format("HH:mm")}
            </p>
            {adjustTarget.status !== "SCHEDULED" && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="已完成或已消课的课次不可调整"
              />
            )}
            <Form
              form={adjustForm}
              layout="vertical"
              disabled={adjustTarget.status !== "SCHEDULED"}
            >
              <Form.Item
                label="上课日期"
                name="sessionDate"
                rules={[{ required: true, message: "请选择上课日期" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                label="开始时间"
                name="startTime"
                rules={[{ required: true, message: "请选择开始时间" }]}
              >
                <TimePicker format="HH:mm" minuteStep={15} style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                label="教室"
                name="classroomId"
                rules={[{ required: true, message: "请选择教室" }]}
              >
                <Select
                  placeholder="选择教室"
                  options={campusRooms.map((r) => ({
                    label: `${r.name}（容量${r.capacity}）`,
                    value: r.id,
                  }))}
                />
              </Form.Item>
            </Form>
            {rulesOfClass.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  该班级排课规则（结束将取消未来课次）
                </Typography.Text>
                <ul style={{ margin: "8px 0 0", paddingLeft: 16 }}>
                  {rulesOfClass.map((r) => (
                    <li key={r.id} style={{ fontSize: 12, marginBottom: 4 }}>
                      {weekdayLabel[r.dayOfWeek]} {r.startTime} ·{" "}
                      {r.classroomName} · 未来 {r.futureScheduledSessionCount}{" "}
                      个课次{" "}
                      {canEndSchedule && r.status === "ACTIVE" && (
                        <Popconfirm
                          title={`确定结束该排课？将取消未来 ${r.futureScheduledSessionCount} 个课次`}
                          okText="确认结束"
                          cancelText="取消"
                          onConfirm={() =>
                            void handleEndRule(
                              r.id,
                              r.futureScheduledSessionCount,
                            )
                          }
                        >
                          <Button size="small" danger>
                            结束排课
                          </Button>
                        </Popconfirm>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </DraggableModal>
    </div>
  );
}

/** 新建周期排课弹窗内层组件：Form/useWatch/预览/提交全量内聚，仅开窗挂载
 *  （destroyOnHidden）——关窗态不再触碰 form 实例，消除「useForm not connected」
 *  开发告警；每次打开经 open-effect 重置（填→关→重开清空语义与迁移前一致）。 */
interface PublishScheduleModalProps {
  open: boolean;
  classes: ClassOption[];
  onClose: () => void;
  /** 发布成功回调：携带软提醒（页面常驻告警条展示）。 */
  onPublished: (warnings: SoftWarningItem[]) => void;
  /** 发布成功回调：刷新周历。 */
  onRefresh: () => Promise<void>;
}

function PublishScheduleModal({
  open,
  classes,
  onClose,
  onPublished,
  onRefresh,
}: PublishScheduleModalProps) {
  const [form] = Form.useForm();
  const formClassId = Form.useWatch("classId", form);
  const formDayOfWeeks: number[] = Form.useWatch("dayOfWeeks", form) ?? [];
  const [dayTimes, setDayTimes] = useState<
    Record<number, { start: Dayjs | null; duration: number }>
  >({});
  const [conflicts, setConflicts] = useState<
    Array<{
      type: ScheduleConflictType;
      dayOfWeek: number;
      description: string;
    }>
  >([]);
  const [submitting, setSubmitting] = useState(false);
  const formClass = classes.find((c) => c.id === formClassId);
  // O-28 校区隔离：教师/教室选项在选定班级后按该班校区服务端拉取（未选班级选项空+引导）；
  // 切换班级即按新校区重拉（表单 onChange 已清已选值）
  const classCampusId = formClass?.campusId ?? null;
  const [modalTeachers, setModalTeachers] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [modalRooms, setModalRooms] = useState<ClassroomOption[]>([]);
  useEffect(() => {
    if (!open || !classCampusId) {
      setModalTeachers([]);
      setModalRooms([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [staffPage, roomPage] = await Promise.all([
          staffApi.listStaffs({
            position: "TEACHER",
            page: 1,
            pageSize: 100,
            campusId: classCampusId,
          }),
          classroomsApi.listClassrooms({
            page: 1,
            pageSize: 100,
            campusId: classCampusId,
            status: "AVAILABLE",
          }),
        ]);
        if (cancelled) return;
        setModalTeachers(staffPage.items.map((s) => ({ id: s.id, name: s.name })));
        setModalRooms(
          roomPage.items.map((r) => ({
            id: r.id,
            name: r.name,
            capacity: r.capacity,
            campusId: r.campusId,
          })),
        );
      } catch {
        if (!cancelled) message.error("教师/教室选项加载失败");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, classCampusId]);

  // 开窗重置（原「排课」按钮的 resetFields 职责内移：Form 已挂载后才可调用）
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setDayTimes({});
    setConflicts([]);
  }, [open, form]);

  // O-1 越界清理：弹窗开着时全局校区变化致班级选项集刷新，已选班级越出新选项集
  // （不属于新校区）即整表复位——班级/教师/教室/星期与每日时间/冲突提示全部清空
  // （对齐补消新建弹窗越界清理语义与 O-21 D5 先例；教师/教室选项本就随班级校区重拉）
  useEffect(() => {
    if (!open) return;
    if (formClassId && !classes.some((c) => c.id === formClassId)) {
      form.resetFields();
      setDayTimes({});
      setConflicts([]);
    }
  }, [open, classes, formClassId, form]);

  /** 发布：提交即预检（原型 submitSchedule 语义）。 */
  async function handlePublish() {
    const values = await form.validateFields();
    const missingDay = (values.dayOfWeeks as number[]).find(
      (d) => !dayTimes[d]?.start,
    );
    if (missingDay !== undefined) {
      message.error(`请设置${weekdayLabel[missingDay]}的开始时间`);
      return;
    }
    setSubmitting(true);
    setConflicts([]);
    try {
      const cls = classes.find((c) => c.id === values.classId)!;
      const result = await publishSchedules({
        classId: values.classId,
        classroomId: values.classroomId ?? cls.defaultClassroomId ?? undefined,
        teacherId: values.teacherId,
        slots: (values.dayOfWeeks as number[]).map((d) => ({
          dayOfWeek: d,
          startTime: dayTimes[d]!.start!.format("HH:mm"),
          durationMinutes: dayTimes[d]!.duration ?? 45,
        })),
        effectiveFrom: (values.effectiveFrom as Dayjs).format("YYYY-MM-DD"),
        effectiveTo: (values.effectiveTo as Dayjs).format("YYYY-MM-DD"),
      });
      onPublished(result.warnings); // 常驻告警条（点「知道了」关闭）；无软提醒时清空
      message.success(
        `发布成功，已生成 ${result.schedules.length} 条排课、${result.sessionCount} 个课次`,
      );
      onClose();
      setDayTimes({});
      await onRefresh();
    } catch (err) {
      if (err instanceof ApiError && err.code === "scheduling.hard_conflict") {
        const detail = err.details as
          | {
              conflicts?: Array<{
                type: ScheduleConflictType;
                dayOfWeek: number;
                description: string;
              }>;
            }
          | undefined;
        setConflicts(detail?.conflicts ?? []);
      } else {
        message.error(
          err instanceof ApiError ? err.message : "发布失败，请稍后重试",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  /** 发布前预览：与后端 expandSchedule 同口径——窗口 = [max(生效起, 班级开课日),
   *  min(生效止, 班级结课日)] 闭区间；YYYY-MM-DD 字符串比较（与
   *  schedule-expansion.service.ts 逐字符同构，规避 DatePicker 值带时间分量）。 */
  const effectiveFrom = Form.useWatch("effectiveFrom", form);
  const effectiveTo = Form.useWatch("effectiveTo", form);
  const previewSessionCount = useMemo(() => {
    if (!formClass || formDayOfWeeks.length === 0 || !effectiveFrom || !effectiveTo)
      return 0;
    const from = (effectiveFrom as Dayjs).format("YYYY-MM-DD");
    const to = (effectiveTo as Dayjs).format("YYYY-MM-DD");
    const windowFrom = from > formClass.startDate ? from : formClass.startDate;
    const windowTo = to < formClass.endDate ? to : formClass.endDate;
    if (windowFrom > windowTo) return 0; // 空窗口（排课有效期与班级有效期无交集）
    let count = 0;
    let cursor = dayjs(windowFrom);
    const end = dayjs(windowTo);
    while (cursor.isBefore(end) || cursor.isSame(end)) {
      if (formDayOfWeeks.includes(cursor.day() === 0 ? 7 : cursor.day()))
        count += 1;
      cursor = cursor.add(1, "day");
    }
    return count;
  }, [formClass, formDayOfWeeks, effectiveFrom, effectiveTo]);

  /** 零课次阻断原因（非 null 时禁用发布并显示 warning）。仅「班级/星期/两日期
   *  齐备但 0 课次」才阻断；缺项时保持按钮可点，让 antd 必填红字校验照常出现。 */
  const zeroSessionBlockReason = useMemo<string | null>(() => {
    if (!formClass || formDayOfWeeks.length === 0 || !effectiveFrom || !effectiveTo)
      return null;
    if ((effectiveFrom as Dayjs).isAfter(effectiveTo as Dayjs, "day"))
      return "有效起始日期不能晚于有效结束日期，请调整后再发布";
    if (previewSessionCount > 0) return null;
    return `当前配置将生成 0 个课次：排课有效期与班级有效期（${formClass.startDate} ~ ${formClass.endDate}）无交集，请调整有效期或更换班级`;
  }, [formClass, formDayOfWeeks, effectiveFrom, effectiveTo, previewSessionCount]);

  // 通知对象摘要（5-5 ADM-19：选定班级加载预览，切班刷新；失败仅隐藏不阻断提交）
  const [notificationPreview, setNotificationPreview] =
    useState<NotificationPreview | null>(null);
  useEffect(() => {
    let cancelled = false;
    setNotificationPreview(null);
    if (!formClassId) return undefined;
    void (async () => {
      try {
        const preview = await getNotificationPreview(formClassId);
        if (!cancelled) setNotificationPreview(preview);
      } catch {
        // 请求失败隐藏摘要（发布提交不受影响，scheduling-workbench-ui:SC-21）
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formClassId]);

  return (
    <DraggableModal
      open={open}
      title="新建周期排课"
      width={520}
      onCancel={onClose}
      onOk={() => void handlePublish()}
      okText="发布"
      cancelText="取消"
      confirmLoading={submitting}
      okButtonProps={{ disabled: zeroSessionBlockReason !== null }}
      destroyOnHidden
    >
      {conflicts.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="存在硬冲突，禁止发布"
          description={
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {conflicts.map((c, i) => (
                <li key={i}>
                  【{conflictTypeLabel[c.type]}·{weekdayLabel[c.dayOfWeek]}】
                  {c.description}
                </li>
              ))}
            </ul>
          }
        />
      )}
      <Form form={form} layout="vertical">
        <Form.Item
          label="班级"
          name="classId"
          rules={[{ required: true, message: "请选择班级" }]}
        >
          <Select
            placeholder="选择班级"
            options={classes.map((c) => ({ label: c.name, value: c.id }))}
            onChange={(v) => {
              const cls = classes.find((c) => c.id === v);
              form.setFieldsValue({
                teacherId: cls?.teacherId,
                classroomId: undefined,
              });
            }}
          />
        </Form.Item>
        <Form.Item
          label="教师"
          name="teacherId"
          rules={[{ required: true, message: "请选择教师" }]}
        >
          <Select
            placeholder={formClass ? "任课教师" : "请先选择班级"}
            showSearch
            optionFilterProp="label"
            options={modalTeachers.map((t) => ({ label: t.name, value: t.id }))}
          />
        </Form.Item>
        <Form.Item
          label="星期（可多选，一次排多天）"
          name="dayOfWeeks"
          rules={[{ required: true, message: "请至少选择一天" }]}
        >
          <Select
            mode="multiple"
            placeholder="选择后可为每天单独设置时间"
            options={Object.entries(weekdayLabel).map(([value, label]) => ({
              value: Number(value),
              label,
            }))}
          />
        </Form.Item>
        {[...formDayOfWeeks].length > 0 && (
          <Form.Item label="每天上课时间（可单独设置）" required>
            <div
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                padding: 12,
              }}
            >
              {[...formDayOfWeeks]
                .sort((a, b) => a - b)
                .map((d) => (
                  <Space
                    key={d}
                    size={12}
                    style={{ display: "flex", marginBottom: 8 }}
                  >
                    <span style={{ width: 40, fontWeight: 600 }}>
                      {weekdayLabel[d]}
                    </span>
                    <TimePicker
                      format="HH:mm"
                      minuteStep={15}
                      style={{ width: 130 }}
                      placeholder="开始时间"
                      value={dayTimes[d]?.start ?? null}
                      onChange={(v) =>
                        setDayTimes((prev) => ({
                          ...prev,
                          [d]: {
                            start: v,
                            duration: prev[d]?.duration ?? 45,
                          },
                        }))
                      }
                    />
                    <InputNumber
                      min={10}
                      max={300}
                      step={5}
                      style={{ width: 130 }}
                      suffix="分钟"
                      placeholder="时长"
                      value={dayTimes[d]?.duration ?? 45}
                      onChange={(v) =>
                        setDayTimes((prev) => ({
                          ...prev,
                          [d]: {
                            start: prev[d]?.start ?? null,
                            duration: v ?? 45,
                          },
                        }))
                      }
                    />
                  </Space>
                ))}
            </div>
          </Form.Item>
        )}
        <Form.Item label="教室（默认沿用班级默认教室）" name="classroomId">
          <Select
            allowClear
            placeholder={formClass ? "默认班级默认教室" : "请先选择班级"}
            options={modalRooms.map((r) => ({
              label: `${r.name}（容量${r.capacity}）`,
              value: r.id,
            }))}
          />
        </Form.Item>
        <Space size={16}>
          <Form.Item
            label="有效起始日期"
            name="effectiveFrom"
            rules={[{ required: true }]}
            initialValue={dayjs()}
          >
            <DatePicker style={{ width: 180 }} />
          </Form.Item>
          <Form.Item
            label="有效结束日期"
            name="effectiveTo"
            rules={[{ required: true }]}
            initialValue={dayjs().add(5, "month")}
          >
            <DatePicker style={{ width: 180 }} />
          </Form.Item>
        </Space>
        {previewSessionCount > 0 && (
          <Alert
            type="info"
            showIcon
            message={`发布后将生成 ${previewSessionCount} 个课次（${formDayOfWeeks.length} 条周期排课），并通知任课教师与在班学生家长`}
          />
        )}
        {zeroSessionBlockReason && (
          <Alert type="warning" showIcon message={zeroSessionBlockReason} />
        )}
        {notificationPreview && (
          <Alert
            type="info"
            showIcon
            message={
              notificationPreview.followedGuardianCount > 0
                ? `通知 ${notificationPreview.followedGuardianCount} 位已关注家长（共 ${notificationPreview.activeStudentCount} 名在班学生）`
                : `该班级暂无已关注公众号的家长（共 ${notificationPreview.activeStudentCount} 名在班学生），发布后将不发送公众号通知`
            }
          />
        )}
      </Form>
    </DraggableModal>
  );
}
